import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import ModelDeployProposalCard, {
  ModelDeployProposal,
  ModelDeployProposalCardHandle,
} from './ModelDeployProposalCard';

import { useProcessedModelLimit } from '../../../hooks/useProcessedModelLimit';

import { RootState } from '../../../store';

interface ModelDeployProposalGroupProps {
  proposals: ModelDeployProposal[];
  conversationId: string;
  messageId: string;
}

const ModelDeployProposalGroup: React.FC<ModelDeployProposalGroupProps> = ({
  proposals,
  conversationId,
  messageId,
}) => {
  const proposalStatuses = useSelector(
    (state: RootState) => state.aiChat.proposalStatuses[conversationId] ?? {}
  );
  const { evaluate: evaluateModelLimit } = useProcessedModelLimit();
  const [isApprovingAll, setIsApprovingAll] = useState(false);
  const cardRefs = useRef<(ModelDeployProposalCardHandle | null)[]>([]);

  // Kept identical to the ids MessageWithChart used before the group existed, so statuses persisted against earlier messages still resolve.
  const messageIds = proposals.map((_, i) => `${messageId}-deploy-${i}`);
  const pendingIds = messageIds.filter((id) => !proposalStatuses[id] || proposalStatuses[id] === 'pending');
  const allSettled = pendingIds.length === 0;

  // Hide Approve All when the tier cap leaves no room — each card already explains why it's locked.
  const limitCheck = evaluateModelLimit();
  const isBlocked = Boolean(limitCheck && !limitCheck.allowed);

  const handleApproveAll = async () => {
    setIsApprovingAll(true);
    // finally, so an unexpected throw can't strand every card disabled with no way back but a reload.
    try {
      // Sequential, unlike the asset flow: each deploy uploads files and writes several records, and the model count only rises once a deploy finishes — so the next card's own cap check sees it.
      for (let i = 0; i < proposals.length; i++) {
        const id = messageIds[i];
        if (proposalStatuses[id] === 'approved' || proposalStatuses[id] === 'declined') continue;
        await cardRefs.current[i]?.approve();
      }
    } finally {
      setIsApprovingAll(false);
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-3">
      {proposals.length > 1 && !allSettled && !isBlocked && (
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={handleApproveAll}
            disabled={isApprovingAll}
            className="eai-btn-cta-toolbar"
          >
            {isApprovingAll && (
              <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            Approve All
          </button>
          <p className="text-xs text-eai-ink-subtle">
            {pendingIds.length} model{pendingIds.length !== 1 ? 's' : ''} still need{pendingIds.length === 1 ? 's' : ''} your approval
          </p>
        </div>
      )}

      {proposals.map((proposal, i) => (
        <ModelDeployProposalCard
          key={messageIds[i]}
          ref={(el) => { cardRefs.current[i] = el; }}
          proposal={proposal}
          conversationId={conversationId}
          messageId={messageIds[i]}
          disabled={isApprovingAll}
        />
      ))}
    </div>
  );
};

export default ModelDeployProposalGroup;
